import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';

import SimpleHeader from 'components/Headers/SimpleHeader.js';
import LoadingButtonCustom from 'views/pages/components/LoadingButtonCustom';
import { compareFilesActions } from 'redux/actions';
import { notificationAlertRef, notify } from 'common';

const PATIENT_FIELD_LABELS = {
  ho_ten: 'Họ tên',
  dien_thoai: 'Điện thoại',
  ma_ho_so: 'Mã hồ sơ',
  tuan_thai: 'Tuần thai',
  ngay_sinh: 'Ngày sinh',
  ngay_nhan_mau: 'Ngày nhận mẫu',
  ngay_tra_ket_qua: 'Ngày trả kết quả',
  noi_thu_mau: 'Nơi thu mẫu',
  bac_si_chi_dinh: 'Bác sĩ chỉ định',
  song_thai: 'Song thai',
};

const formatPairNo = (no) => String(no ?? 0).padStart(2, '0');
const OUTLINE_BORDER_COLOR = '#8898aa';

const ellipsizeMiddle = (value, head = 18, tail = 18) => {
  const s = String(value ?? '');
  if (!s) return '';
  if (s.length <= head + tail + 3) return s;
  return `${s.slice(0, head)}...${s.slice(-tail)}`;
};

function CompareFiles() {
  const dispatch = useDispatch();

  const excelInputRefs = useRef([null, null]);
  const pdfInputARef = useRef(null);
  const pdfInputBRef = useRef(null);

  const [excelFiles, setExcelFiles] = useState([null, null]); // [excel1, excel2]
  const [pairs, setPairs] = useState([]); // [{id,pdfA,pdfB,result,inProgress,summary,tables,response,comparedAt}]
  const [selectedId, setSelectedId] = useState(null);
  const nextPairNoRef = useRef(1);

  const [pairModal, setPairModal] = useState({
    isOpen: false,
    pdfA: null,
    pdfB: null,
  });

  // Sequential runner: stop-after-current behavior.
  const [runner, setRunner] = useState({
    running: false,
    stopRequested: false,
    currentId: null,
    mode: null, // 'new' | 'all'
  });
  const runningRef = useRef(false);
  const stopRequestedRef = useRef(false);

  const pairsRef = useRef(pairs);
  useEffect(() => {
    pairsRef.current = pairs;
    // Keep monotonically increasing pair number even if user deletes/reorders.
    const maxNo = pairs.reduce((m, p) => Math.max(m, p?.no ?? 0), 0);
    nextPairNoRef.current = Math.max(nextPairNoRef.current, maxNo + 1);
  }, [pairs]);

  const hasTrustedExcel = useMemo(
    () => !!(excelFiles?.[0] && excelFiles?.[1]),
    [excelFiles]
  );

  const selectedPair = useMemo(
    () => pairs.find((p) => p.id === selectedId) ?? null,
    [pairs, selectedId]
  );

  // NOTE: kept as reference (previously used for disabling buttons).
  // const pairsNewCount = useMemo(
  //   () => pairs.filter((p) => !p?.inProgress && !p?.result).length,
  //   [pairs]
  // );
  // const pairsAnyCount = useMemo(
  //   () => pairs.filter((p) => !p?.inProgress).length,
  //   [pairs]
  // );

  const resetExcelInput = (idx) => {
    if (excelInputRefs.current?.[idx]) {
      excelInputRefs.current[idx].value = '';
    }
  };

  const clearExcel = () => {
    if (runner.running) return;
    setExcelFiles([null, null]);
    resetExcelInput(0);
    resetExcelInput(1);
  };

  const openAddPairModal = () => {
    if (runner.running) return;
    setPairModal({ isOpen: true, pdfA: null, pdfB: null });
    if (pdfInputARef.current) pdfInputARef.current.value = '';
    if (pdfInputBRef.current) pdfInputBRef.current.value = '';
  };

  const closeAddPairModal = () => {
    setPairModal((prev) => ({ ...prev, isOpen: false }));
  };

  const addPair = () => {
    if (!pairModal.pdfA || !pairModal.pdfB) return;
    const id = uuidv4();
    const no = nextPairNoRef.current;
    nextPairNoRef.current += 1;
    const next = {
      id,
      no,
      pdfA: pairModal.pdfA,
      pdfB: pairModal.pdfB,
      result: null, // 'match' | 'mismatch'
      inProgress: false,
      summary: { matched: 0, mismatch: 0, missing: 0 },
      tables: [],
      response: null,
      comparedAt: null,
    };
    setPairs((prev) => [next, ...prev]);
    setSelectedId(id);
    closeAddPairModal();
  };

  const removePair = (id) => {
    if (runner.running) return;
    setPairs((prev) => prev.filter((p) => p.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const mapFieldLabel = (field) => PATIENT_FIELD_LABELS[field] ?? field ?? '';

  const buildResultFromApi = (apiRes) => {
    const pdfs = Array.isArray(apiRes?.pdfs) ? apiRes.pdfs : [];
    const tables = pdfs.map((pdfItem) => {
      const pv = pdfItem?.pdf_vs_excel ?? {};
      const rowsMatched = [];
      const rowsMismatch = [];
      const rowsMissing = [];

      const infoDetails = pv?.patient_info_match?.details ?? {};
      Object.entries(infoDetails).forEach(([fieldKey, info]) => {
        const excelVal =
          typeof info?.excel_expected === 'string' && info?.excel_expected !== ''
            ? info.excel_expected
            : info?.excel ?? '';
        rowsMatched.push([
          mapFieldLabel(fieldKey),
          String(excelVal ?? ''),
          String(info?.pdf ?? ''),
          'Khớp',
        ]);
      });

      const zComparisons = Array.isArray(pv?.z_scores?.comparisons)
        ? pv.z_scores.comparisons
        : [];
      zComparisons.forEach((comp) => {
        rowsMatched.push([
          String(comp?.field ?? ''),
          String(comp?.excel ?? ''),
          String(comp?.pdf ?? ''),
          'Khớp',
        ]);
      });

      const errors = Array.isArray(pv?.errors) ? pv.errors : [];
      errors.forEach((err) => {
        if (typeof err === 'string') {
          rowsMissing.push(['Thiếu dữ liệu', '', err, 'Thiếu']);
          return;
        }
        if (!err || typeof err !== 'object') return;
        rowsMismatch.push([
          mapFieldLabel(err?.field),
          String(err?.excel ?? ''),
          String(err?.pdf ?? ''),
          'Không khớp',
        ]);
      });

      const rows = [...rowsMismatch, ...rowsMissing, ...rowsMatched];
      const summary = {
        matched: rowsMatched.length,
        mismatch: rowsMismatch.length,
        missing: rowsMissing.length,
      };

      return {
        pdf: pdfItem?.pdf ?? '',
        labcode: pdfItem?.labcode ?? '',
        summary,
        rows,
      };
    });

    const summaryTotal = tables.reduce(
      (acc, t) => ({
        matched: acc.matched + (t?.summary?.matched ?? 0),
        mismatch: acc.mismatch + (t?.summary?.mismatch ?? 0),
        missing: acc.missing + (t?.summary?.missing ?? 0),
      }),
      { matched: 0, mismatch: 0, missing: 0 }
    );

    const isOk =
      pdfs.length > 0 &&
      pdfs.every((item) => {
        const pv = item?.pdf_vs_excel ?? {};
        const errs = Array.isArray(pv?.errors) ? pv.errors : [];
        return pv?.info_match === true && pv?.scores_match === true && errs.length === 0;
      });

    return {
      result: isOk ? 'match' : 'mismatch',
      summaryTotal,
      tables,
    };
  };

  const dispatchCompare = (payload) =>
    new Promise((resolve, reject) => {
      dispatch(
        compareFilesActions.compareFiles(payload, {
          success: (data) => resolve(data),
          failed: (mess) => reject(mess),
        })
      );
    });

  const statusText = (p) => {
    if (p?.inProgress) return '--';
    if (!p?.result) return '--';
    return p.result === 'match' ? 'Khớp' : 'Không khớp';
  };

  const statusColor = (p) => {
    if (p?.inProgress) return 'warning';
    if (!p?.result) return 'secondary';
    return p.result === 'match' ? 'success' : 'danger';
  };

  const retryTitle = (p) => (p?.result ? 'Thử lại' : 'Bắt đầu so sánh');

  const compareOnePair = async (id) => {
    const p = pairsRef.current.find((x) => x.id === id);
    if (!p || p.inProgress) return;

    if (!hasTrustedExcel) {
      notify(
        notificationAlertRef,
        'warning',
        'Thông báo',
        'Bạn cần chọn đủ 2 file Excel nguồn chuẩn trước khi so sánh.'
      );
      return;
    }

    setPairs((prev) =>
      prev.map((item) => (item.id === id ? { ...item, inProgress: true } : item))
    );

    try {
      const apiRes = await dispatchCompare({
        excel1: excelFiles[0],
        excel2: excelFiles[1],
        pdf1: p.pdfA,
        pdf2: p.pdfB,
      });

      const built = buildResultFromApi(apiRes);
      setPairs((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                inProgress: false,
                result: built.result,
                summary: built.summaryTotal,
                tables: built.tables,
                response: apiRes,
                comparedAt: Date.now(),
              }
            : item
        )
      );
    } catch (error) {
      setPairs((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                inProgress: false,
                result: 'mismatch',
                summary: { matched: 0, mismatch: 1, missing: 0 },
                tables: [
                  {
                    pdf: '',
                    labcode: '',
                    summary: { matched: 0, mismatch: 1, missing: 0 },
                    rows: [['Lỗi', '', String(error ?? 'Không xác định'), 'Lỗi']],
                  },
                ],
                response: null,
                comparedAt: Date.now(),
              }
            : item
        )
      );
      notify(
        notificationAlertRef,
        'danger',
        'Thông báo',
        `So sánh thất bại: ${error ?? 'Không xác định'}`
      );
    }
  };

  const requestStopAfterCurrent = () => {
    if (!runner.running) return;
    stopRequestedRef.current = true;
    setRunner((prev) => ({ ...prev, stopRequested: true }));
    notify(
      notificationAlertRef,
      'warning',
      'Thông báo',
      'Đã yêu cầu dừng. Hệ thống sẽ dừng sau khi so sánh xong cặp hiện tại.'
    );
  };

  const runQueue = async (queueIds, mode) => {
    if (!queueIds.length) return;
    if (!hasTrustedExcel) {
      notify(
        notificationAlertRef,
        'warning',
        'Thông báo',
        'Bạn cần chọn đủ 2 file Excel nguồn chuẩn trước khi so sánh.'
      );
      return;
    }
    if (runningRef.current) return;

    runningRef.current = true;
    stopRequestedRef.current = false;
    setRunner({
      running: true,
      stopRequested: false,
      currentId: null,
      mode,
    });

    try {
      for (const id of queueIds) {
        if (stopRequestedRef.current) break;
        setRunner((prev) => ({ ...prev, currentId: id }));
        await compareOnePair(id);
        setRunner((prev) => ({ ...prev, currentId: null }));
      }
    } finally {
      runningRef.current = false;
      stopRequestedRef.current = false;
      setRunner({
        running: false,
        stopRequested: false,
        currentId: null,
        mode: null,
      });
    }
  };

  const handleCompareNewPairs = () => {
    if (runner.running) {
      requestStopAfterCurrent();
      return;
    }
    const queue = pairs.filter((p) => !p.inProgress && !p.result).map((p) => p.id);
    if (!queue.length) {
      notify(notificationAlertRef, 'warning', 'Thông báo', 'Không có cặp mới để so sánh.');
      return;
    }
    runQueue(queue, 'new');
  };

  const handleCompareAllPairs = () => {
    if (runner.running) {
      requestStopAfterCurrent();
      return;
    }
    const queue = pairs.filter((p) => !p.inProgress).map((p) => p.id);
    if (!queue.length) {
      notify(notificationAlertRef, 'warning', 'Thông báo', 'Không có cặp nào để so sánh.');
      return;
    }
    runQueue(queue, 'all');
  };

  return (
    <>
      <SimpleHeader />
      <Container className="mt--9" fluid>
        <Row>
          <Col lg="5">
            <Card className="mb-3">
              <CardHeader className="pb-2 d-flex justify-content-between rounded border-bottom align-items-center">
                <h3 className="mb-0">Excel nguồn chuẩn</h3>
                <div className="d-flex align-items-center">
                  <small className="text-muted mr-3">
                    {hasTrustedExcel
                      ? 'Đã chọn đủ 2 file'
                      : excelFiles.some(Boolean)
                      ? 'Chưa chọn đủ 2 file'
                      : 'Chưa chọn file'}
                  </small>
                  <Button
                    color="secondary"
                    size="sm"
                    outline
                    disabled={runner.running}
                    onClick={clearExcel}
                    style={{ borderColor: OUTLINE_BORDER_COLOR }}
                  >
                    Xóa tất cả
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                {[0, 1].map((idx) => {
                  const file = excelFiles[idx];
                  return (
                    <div key={`excel-${idx}`} className="d-flex align-items-center mb-2 flex-wrap">
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        disabled={runner.running}
                        onClick={() => excelInputRefs.current[idx]?.click()}
                        style={{ borderColor: OUTLINE_BORDER_COLOR }}
                      >
                        Chọn Excel {idx + 1} <span style={{ color: 'red' }}>*</span>
                      </Button>
                      <input
                        ref={(el) => {
                          excelInputRefs.current[idx] = el;
                        }}
                        type="file"
                        accept=".xlsx,.xls"
                        style={{ display: 'none' }}
                        disabled={runner.running}
                        onChange={(e) => {
                          const picked = e?.target?.files?.[0] ?? null;
                          setExcelFiles((prev) => {
                            const next = [...prev];
                            next[idx] = picked;
                            return next;
                          });
                        }}
                      />
                        {file ? (
                          <div className="ml-3 d-flex align-items-center">
                            <small className="text-muted" title={file.name}>
                              {ellipsizeMiddle(file.name, 20, 16)}
                            </small>
                            {/*
                            <Button
                              close
                              className="ml-2"
                              disabled={runner.running}
                              onClick={() => {
                                setExcelFiles((prev) => {
                                  const next = [...prev];
                                  next[idx] = null;
                                  return next;
                                });
                                resetExcelInput(idx);
                              }}
                              style={{ marginTop: -1 }}
                            />
                            */}
                          </div>
                        ) : (
                          <small className="text-muted ml-3">Chưa có file</small>
                        )}
                    </div>
                  );
                })}

                <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                  Gợi ý: Excel 1 là file thông tin thai phụ. Excel 2 là file phụ lục tính toán &amp; kết quả xét nghiệm.
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader className="pb-2 d-flex justify-content-between rounded border-bottom align-items-center flex-wrap">
                <h3 className="mb-0">Cặp PDF cần đối chiếu</h3>
                <div className="d-flex align-items-center flex-wrap">
                  <Button
                    color="secondary"
                    outline
                    size="sm"
                    className="mr-2"
                    onClick={openAddPairModal}
                    disabled={runner.running}
                    style={{ borderColor: '#8898aa' }}
                  >
                    Thêm cặp PDF
                  </Button>
                  {/*
                  <Badge color="info" className="mr-3">
                    Tổng cặp: {pairs.length}
                  </Badge>
                  */}

                  <LoadingButtonCustom
                    onClick={handleCompareNewPairs}
                    color="info"
                    type="button"
                    className="text-nowrap mr-2"
                    loading={false}
                    style={{ borderColor: '#8898aa' }}
                  >
                    {runner.running ? 'Dừng' : 'So sánh các cặp mới'}
                  </LoadingButtonCustom>

                  <LoadingButtonCustom
                    onClick={handleCompareAllPairs}
                    color="secondary"
                    outline
                    type="button"
                    className="text-nowrap"
                    loading={false}
                    style={{ borderColor: '#8898aa' }}
                  >
                    {runner.running ? 'Dừng' : 'So sánh tất cả'}
                  </LoadingButtonCustom>
                </div>
              </CardHeader>
              <CardBody>
                {pairs.length === 0 ? (
                  <div className="text-muted">
                    Chưa có cặp PDF nào. Bấm “Thêm cặp PDF” để bắt đầu.
                  </div>
                ) : (
                  pairs.map((p) => {
                    const isActive = p.id === selectedId;
                    const disableActions =
                      p.inProgress ||
                      (runner.running && runner.currentId && runner.currentId !== p.id);
                    const pairLabel = `Cặp ${formatPairNo(p?.no)}`;
                    return (
                      <div
                        key={p.id}
                        className={`border rounded p-2 mb-2 ${isActive ? 'border-info' : ''}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => setSelectedId(p.id)}
                      >
                        <div className="d-flex justify-content-between align-items-start">
                          <div style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center flex-wrap">
                              <strong className="mr-2">{pairLabel}</strong>
                              <Badge color={statusColor(p)}>{statusText(p)}</Badge>
                              {p.inProgress && (
                                <span className="text-muted ml-2" style={{ fontSize: 12 }}>
                                  Đang so sánh...
                                </span>
                              )}
                            </div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              <code title={p.pdfA?.name ?? ''}>
                                PDF A: {ellipsizeMiddle(p.pdfA?.name ?? '-', 18, 14)}
                              </code>
                            </div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              <code title={p.pdfB?.name ?? ''}>
                                PDF B: {ellipsizeMiddle(p.pdfB?.name ?? '-', 18, 14)}
                              </code>
                            </div>
                          </div>

                          <div className="d-flex">
                            {/*
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary mr-2"
                              title="Xem chi tiết"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setSelectedId(p.id);
                              }}
                              style={{ borderColor: '#8898aa' }}
                            >
                              <i className="fas fa-eye" />
                            </button>
                            */}

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary mr-2"
                              title={retryTitle(p)}
                              disabled={disableActions}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                compareOnePair(p.id);
                              }}
                              style={{ borderColor: '#8898aa' }}
                            >
                              <i className={p?.result ? 'fas fa-redo' : 'fas fa-play'} />
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Xóa"
                              disabled={disableActions}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                removePair(p.id);
                              }}
                              style={{ borderColor: '#f5365c' }}
                            >
                              <i className="fas fa-trash" />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </CardBody>
            </Card>
          </Col>

          <Col lg="7">
            <Card>
              <CardHeader className="pb-2 d-flex justify-content-between rounded border-bottom align-items-center">
                <h3 className="mb-0">Kết quả so sánh</h3>
                {selectedPair && (
                  <div className="d-flex">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary mr-2"
                      title={retryTitle(selectedPair)}
                      disabled={selectedPair.inProgress || runner.running}
                      onClick={() => compareOnePair(selectedPair.id)}
                      style={{ borderColor: '#8898aa' }}
                    >
                      <i
                        className={selectedPair?.result ? 'fas fa-redo' : 'fas fa-play'}
                      />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      title="Xóa"
                      disabled={selectedPair.inProgress || runner.running}
                      onClick={() => removePair(selectedPair.id)}
                      style={{ borderColor: '#f5365c' }}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </div>
                )}
              </CardHeader>
              <CardBody>
                {!selectedPair ? (
                  <div className="text-muted">
                    Chưa chọn cặp PDF. Chọn 1 cặp ở bên trái để xem chi tiết, hoặc bấm “Thêm cặp PDF”.
                    <div className="mt-3">
                      <Button
                        color="secondary"
                        outline
                        size="sm"
                        onClick={openAddPairModal}
                        disabled={runner.running}
                        style={{ borderColor: OUTLINE_BORDER_COLOR }}
                      >
                        Thêm cặp PDF
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="d-flex align-items-center flex-wrap mb-3">
                      <strong className="mr-2">
                        {`Cặp ${formatPairNo(selectedPair?.no)}`}
                      </strong>
                      <Badge color={statusColor(selectedPair)}>{statusText(selectedPair)}</Badge>
                      {selectedPair.inProgress && (
                        <span className="text-muted ml-2" style={{ fontSize: 12 }}>
                          Đang so sánh...
                        </span>
                      )}
                    </div>

                    <Row className="mb-3">
                      <Col xs={!!(selectedPair.summary?.missing ?? 0) ? '4' : '6'}>
                        <div className="border rounded p-2 text-center">
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            Khớp
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 600 }}>
                            {selectedPair.summary?.matched ?? 0}
                          </div>
                        </div>
                      </Col>
                      <Col xs={!!(selectedPair.summary?.missing ?? 0) ? '4' : '6'}>
                        <div className="border rounded p-2 text-center">
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            Không khớp
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 600 }}>
                            {selectedPair.summary?.mismatch ?? 0}
                          </div>
                        </div>
                      </Col>
                      {!!(selectedPair.summary?.missing ?? 0) && (
                        <Col xs="4">
                          <div className="border rounded p-2 text-center">
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              Thiếu
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 600 }}>
                              {selectedPair.summary?.missing ?? 0}
                            </div>
                          </div>
                        </Col>
                      )}
                    </Row>

                    {selectedPair.tables?.length ? (
                      selectedPair.tables.map((t, tidx) => (
                        <div key={`${selectedPair.id}-tbl-${tidx}`} className="mb-4">
                          <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              <strong className="text-dark" title={t?.pdf ?? ''}>
                                {ellipsizeMiddle(t?.pdf || `PDF ${tidx + 1}`, 26, 16)}
                              </strong>
                              {!!t?.labcode && (
                                <span className="ml-2">
                                  <Badge color="light">{t.labcode}</Badge>
                                </span>
                              )}
                            </div>
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              Khớp: {t?.summary?.matched ?? 0} · Không khớp:{' '}
                              {t?.summary?.mismatch ?? 0}
                              {!!(t?.summary?.missing ?? 0) &&
                                ` · Thiếu: ${t?.summary?.missing ?? 0}`}
                            </div>
                          </div>

                          <div className="table-responsive">
                            <table className="table table-sm table-striped">
                              <thead>
                                <tr>
                                  <th>Trường</th>
                                  <th>Excel</th>
                                  <th>PDF</th>
                                </tr>
                              </thead>
                              <tbody>
                                {t?.rows?.length ? (
                                  t.rows.map((r, ridx) => (
                                    <tr
                                      key={`${selectedPair.id}-tbl-${tidx}-row-${ridx}`}
                                      className={
                                        r[3] === 'Không khớp' || r[3] === 'Lỗi'
                                          ? 'table-danger'
                                          : r[3] === 'Thiếu'
                                          ? 'table-warning'
                                          : ''
                                      }
                                    >
                                      <td>{r[0]}</td>
                                      <td>{r[1]}</td>
                                      <td>{r[2]}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={3} className="text-muted">
                                      Chưa có dữ liệu so sánh.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-muted">Chưa có dữ liệu so sánh.</div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={pairModal.isOpen} toggle={closeAddPairModal} centered>
        <ModalHeader toggle={closeAddPairModal}>Thêm cặp PDF</ModalHeader>
        <ModalBody>
          <div className="mb-2 d-flex align-items-center flex-wrap">
            <Button
              color="secondary"
              outline
              size="sm"
              onClick={() => pdfInputARef.current?.click()}
              style={{ borderColor: OUTLINE_BORDER_COLOR }}
            >
              Chọn PDF A <span style={{ color: 'red' }}>*</span>
            </Button>
            <input
              ref={pdfInputARef}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const picked = e?.target?.files?.[0] ?? null;
                setPairModal((prev) => ({ ...prev, pdfA: picked }));
              }}
            />
            {pairModal.pdfA ? (
              <div className="ml-3 d-flex align-items-center">
                <small className="text-muted" title={pairModal.pdfA.name}>
                  {ellipsizeMiddle(pairModal.pdfA.name, 24, 16)}
                </small>
                <button
                  type="button"
                  className="close ml-2"
                  onClick={() => {
                    setPairModal((prev) => ({ ...prev, pdfA: null }));
                    if (pdfInputARef.current) pdfInputARef.current.value = '';
                  }}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            ) : (
              <small className="text-muted ml-3">Chưa có file</small>
            )}
          </div>

          <div className="mb-2 d-flex align-items-center flex-wrap">
            <Button
              color="secondary"
              outline
              size="sm"
              onClick={() => pdfInputBRef.current?.click()}
              style={{ borderColor: OUTLINE_BORDER_COLOR }}
            >
              Chọn PDF B <span style={{ color: 'red' }}>*</span>
            </Button>
            <input
              ref={pdfInputBRef}
              type="file"
              accept="application/pdf,.pdf"
              style={{ display: 'none' }}
              onChange={(e) => {
                const picked = e?.target?.files?.[0] ?? null;
                setPairModal((prev) => ({ ...prev, pdfB: picked }));
              }}
            />
            {pairModal.pdfB ? (
              <div className="ml-3 d-flex align-items-center">
                <small className="text-muted" title={pairModal.pdfB.name}>
                  {ellipsizeMiddle(pairModal.pdfB.name, 24, 16)}
                </small>
                <button
                  type="button"
                  className="close ml-2"
                  onClick={() => {
                    setPairModal((prev) => ({ ...prev, pdfB: null }));
                    if (pdfInputBRef.current) pdfInputBRef.current.value = '';
                  }}
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>
            ) : (
              <small className="text-muted ml-3">Chưa có file</small>
            )}
          </div>

          <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
            Gợi ý: PDF B là file appendix.
          </div>

          <div className="d-flex justify-content-end mt-4">
            <Button
              color="secondary"
              outline
              className="mr-2"
              onClick={closeAddPairModal}
              style={{ borderColor: OUTLINE_BORDER_COLOR }}
            >
              Hủy
            </Button>
            <Button color="info" disabled={!pairModal.pdfA || !pairModal.pdfB} onClick={addPair}>
              Thêm cặp
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}

export default memo(CompareFiles);
